import { type FC, useCallback, useMemo, useState } from 'react';

import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import type { IconTypes } from '@vakantiesnl/components/src/atoms/Icon';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { StepTitle } from '@vakantiesnl/components/src/blocks/accommodation/StepTitle/StepTitle';
import { getExtraPriceTitlePerPerson } from '@vakantiesnl/components/src/molecules/FlightSelector/helpers';
import { ModalOrDrawer } from '@vakantiesnl/components/src/molecules/ModalOrDrawer/ModalOrDrawer';
import { RichText } from '@vakantiesnl/components/src/molecules/RichText';
import { SingleSelect } from '@vakantiesnl/components/src/molecules/SelectBar';
import { getBoardDescription } from '@vakantiesnl/components/src/utils/accommodation';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAccommodationContext } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useRichText } from '@vakantiesnl/services/src/hooks/contentful';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { type Search } from '@vakantiesnl/types';
import dynamic from 'next/dynamic';

const CompassLoader = dynamic(
	() =>
		import('@vakantiesnl/components/src/__LEGACY__/atoms/CompassLoader').then((mod) => ({
			default: mod.CompassLoader,
		})),
	{ ssr: false },
);

import { useCustomStyles } from './MealPlan.styles';

const rteFilter = {
	['fields.key[in]']: ['mealplan.info'],
};

type RoomCodeMapping = {
	icon: IconTypes;
	paragraph: string;
};

const MealPlan: FC = () => {
	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const { accommodation } = useGetAccommodationByEntityId();
	const {
		state: { mealPlanData, roomData, flightData, tripsOfferData },
		dispatch: { setMealPlanOffer },
	} = useAccommodationContext();
	const {
		dispatch: { updateSelectedMealPlan },
	} = useTripFilterContext();
	const [showModal, setShowModal] = useState<boolean>(false);

	const mappedRichTextElements = useRichText(rteFilter);
	const document = mappedRichTextElements && mappedRichTextElements['mealplan.info'];

	const toggleMealplanInfoModal = useCallback(() => {
		setShowModal(!showModal);

		if (!showModal) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ onMealplanModalOpen }) =>
					track(onMealplanModalOpen(accommodation)),
				);
			});
		}
	}, [showModal, accommodation]);

	const onMealPlanSelect = useCallback(
		(offer: Search.OfferItem, label: string, priceDifference: number | undefined) => (): void => {
			setMealPlanOffer(offer);
			updateSelectedMealPlan(offer.rooms[0].board.code);

			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ mealPlanSelect }) => {
					track(mealPlanSelect(accommodation, offer, label, priceDifference));
				}),
			);
		},
		[setMealPlanOffer, updateSelectedMealPlan, accommodation],
	);

	const roomCodeMapping: Record<string, RoomCodeMapping> = useMemo(
		() => ({
			AO: { icon: 'room', paragraph: microCopies['mealplan_paragraphText.AO'] },
			BB: { icon: 'breakfast', paragraph: microCopies['mealplan_paragraphText.BB'] },
			HB: { icon: 'halfBoard', paragraph: microCopies['mealplan_paragraphText.HB'] },
			FB: { icon: 'fullBoard', paragraph: microCopies['mealplan_paragraphText.FB'] },
			AI: { icon: 'allInclusive', paragraph: microCopies['mealplan_paragraphText.AI'] },
		}),
		[microCopies],
	);

	if (mealPlanData?.loading || roomData?.loading || flightData?.loading || tripsOfferData?.loading) {
		return (
			<div data-cy="meal-plan-selector">
				<StepTitle className={classes.titleWrapper} title={microCopies['accommodation.meal_plan.title']} />
				<CompassLoader />
			</div>
		);
	} else if (mealPlanData === null || !mealPlanData.mealPlanOffers.length) {
		return (
			<div data-cy="meal-plan-selector">
				<StepTitle className={classes.titleWrapper} title={microCopies['accommodation.meal_plan.title']} />
				<Alert variant="warning" text={microCopies['mealfilters.noresults']} />
			</div>
		);
	}

	const previousSelectedOfferPrice = mealPlanData?.previousSelectedOffer?.pricePerAdult || 0;
	return (
		<div data-cy="meal-plan-selector">
			<div className={classes.titleWrapper}>
				<StepTitle title={microCopies['accommodation.meal_plan.title']} />
			</div>
			{mealPlanData.mealPlanOffers.map((offer, key) => {
				const priceDifference =
					previousSelectedOfferPrice - offer.pricePerAdult === 0
						? undefined
						: offer.pricePerAdult - previousSelectedOfferPrice;

				const optionTitle: string = getBoardDescription(offer, microCopies);

				const roomCode = offer.rooms[0];

				return (
					<SingleSelect
						key={`mealplan-${key}`}
						title={optionTitle}
						selected={offer.id === mealPlanData.selected}
						id={`mealplan-${offer.id}`}
						value={offer.id}
						name="mealplan"
						dataCy="mealplanOption"
						tag={
							<Tag color={'orange'} variant={priceDifference ? 'outline' : 'filled'} size="small">
								{priceDifference
									? getExtraPriceTitlePerPerson(priceDifference)
									: microCopies['cheapestOption']}
							</Tag>
						}
						icon={roomCodeMapping[roomCode.board.code].icon}
						onSelect={onMealPlanSelect(offer, optionTitle, priceDifference)}
					>
						<Typography variant="bodySm" as="p" data-cy="mealplanOption-paragraph">
							{roomCodeMapping[roomCode.board.code].paragraph}
						</Typography>
					</SingleSelect>
				);
			})}
			{showModal && document && (
				<ModalOrDrawer
					title={microCopies['mealplan.info.title']}
					open={showModal}
					onClose={toggleMealplanInfoModal}
					hasCloseIcon
				>
					<RichText document={document} />
				</ModalOrDrawer>
			)}
		</div>
	);
};

export default MealPlan;
